import { useId, useRef, useState } from 'react'
import { useDismiss } from '../../lib/useDismiss'
import { FlagIcon } from '../icons'

export type PageWarning = {
  id: string
  /** 'warn' is something to act on; 'info' is a standing fact worth knowing. */
  tone: 'warn' | 'info'
  text: React.ReactNode
}

/**
 * Warnings as a flag with a count, expanded on demand (137, David 9 Oct:
 * "don't give them a bunch of space at the top of the page full time").
 *
 * The one-administrator notice used to be a full-width box above the roster on
 * every visit, pushing the table down for a sentence the reader had already
 * read. It is still ONE press away and the count says it is there, which is
 * the line between tucked away and hidden: a flag that shows nothing when
 * there is nothing, and a number when there is.
 *
 * The count is the number of warnings, never a severity score. 'warn' tints
 * the flag; 'info' alone leaves it quiet.
 *
 * Not a Dialog: it is a menu-weight surface that closes on outside click and
 * Esc (useDismiss), the same as the picker and the bell.
 */
export function WarningsFlag({ warnings }: { warnings: PageWarning[] }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  useDismiss(wrapRef, open, (viaKeyboard) => {
    setOpen(false)
    if (viaKeyboard) btnRef.current?.focus()
  })

  if (warnings.length === 0) return null
  const warn = warnings.some((w) => w.tone === 'warn')
  const n = warnings.length

  return (
    <div className="warnflag" ref={wrapRef}>
      <button
        type="button"
        ref={btnRef}
        className={`warnflag__btn${warn ? ' warnflag__btn--warn' : ''}`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`${n} ${n === 1 ? 'notice' : 'notices'}`}
        title={`${n} ${n === 1 ? 'notice' : 'notices'}`}
        onClick={() => setOpen((o) => !o)}
      >
        <FlagIcon size={15} />
        <span className="warnflag__n">{n}</span>
      </button>

      {open && (
        <div className="warnflag__panel" id={panelId} role="region" aria-label="Notices">
          <ul className="warnflag__list">
            {warnings.map((w) => (
              <li key={w.id} className={`warnflag__item warnflag__item--${w.tone}`}>
                {w.text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
